/**
 * Reticule geometry + limb pick. Skill-driven drift over an idealized
 * body silhouette. The same math backs the accuracy panel (visual
 * reticule position) and the fire path (which limb / miss the shot
 * resolves to).
 *
 * Coordinates are in "body-panel space" — the silhouette is centered at
 * (0, 0) and roughly bounded by (-0.5, -0.9) to (0.5, 0.9).
 *
 * Headless — no PixiJS.
 */

import { RETICLE_DRIFT_RADIUS, RETICLE_DRIFT_SPEED } from '../render/style';
import type { BodyPart } from './body';

export interface Point2 {
  x: number;
  y: number;
}

/** Axis-aligned body-part box in body-panel space. */
interface LimbBox {
  part: BodyPart;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Body silhouette used by BOTH the panel renderer and the limb picker.
 * Order matters: earlier boxes win when the reticule is over overlapping
 * regions. Layout is a stylized humanoid — head at top, torso center,
 * arms flanking, legs below.
 */
export const BODY_LAYOUT: readonly LimbBox[] = [
  { part: 'head', x0: -0.12, y0: -0.9, x1: 0.12, y1: -0.55 },
  { part: 'leftArm', x0: -0.5, y0: -0.45, x1: -0.28, y1: 0.15 },
  { part: 'rightArm', x0: 0.28, y0: -0.45, x1: 0.5, y1: 0.15 },
  { part: 'torso', x0: -0.28, y0: -0.55, x1: 0.28, y1: 0.15 },
  { part: 'leftLeg', x0: -0.22, y0: 0.15, x1: -0.02, y1: 0.9 },
  { part: 'rightLeg', x0: 0.02, y0: 0.15, x1: 0.22, y1: 0.9 },
];

/**
 * Deterministic reticule position for a given accumulated drift time.
 * Lissajous curve on x (1.3× frequency) and y (1.0× frequency) so the
 * reticule traces an organic non-repeating loop rather than a plain
 * circle. Radius optionally scales with a distance factor (0 = point-
 * blank, 1 = max weapon range) so far shots wander more.
 */
export function computeReticulePosition(driftT: number, distanceFactor = 0): Point2 {
  const skillRadius = RETICLE_DRIFT_RADIUS;
  const distScale = 1 + distanceFactor * 1.6;
  const skillSpeed = RETICLE_DRIFT_SPEED;
  const speedScale = 1 + distanceFactor * 0.9;
  return {
    x: skillRadius * distScale * Math.sin(skillSpeed * speedScale * driftT * 1.3),
    y: skillRadius * distScale * Math.cos(skillSpeed * speedScale * driftT),
  };
}

/** Center-of-limb point for a given BODY_LAYOUT index. Used when the
 *  player has cycled to a specific limb with arrow keys — reticle jumps
 *  to that limb's center instead of drifting. */
export function limbCenter(idx: number): Point2 {
  const box = BODY_LAYOUT[idx];
  if (!box) return { x: 0, y: 0 };
  return { x: (box.x0 + box.x1) / 2, y: (box.y0 + box.y1) / 2 };
}

/**
 * Which limb (if any) sits under a reticule position. Returns null if the
 * reticule is outside every limb box — a pure miss.
 */
export function limbAtReticule(pt: Point2): BodyPart | null {
  for (const b of BODY_LAYOUT) {
    if (pt.x >= b.x0 && pt.x <= b.x1 && pt.y >= b.y0 && pt.y <= b.y1) return b.part;
  }
  return null;
}

/** Total silhouette bounds — used by the accuracy-panel renderer. */
export function silhouetteBounds(): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of BODY_LAYOUT) {
    if (b.x0 < x0) x0 = b.x0;
    if (b.y0 < y0) y0 = b.y0;
    if (b.x1 > x1) x1 = b.x1;
    if (b.y1 > y1) y1 = b.y1;
  }
  return { x0, y0, x1, y1 };
}
